
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface MetricCardProps {
  label: string;
  value: number;
  previousValue?: number;
  format?: "number" | "percent" | "duration" | "decimal";
  icon?: React.ReactNode;
  subtitle?: string;
}

function formatValue(value: number, fmt: string = "number"): string {
  switch (fmt) {
    case "percent":
      return `${(value * 100).toFixed(1)}%`;
    case "decimal":
      return value.toFixed(1);
    case "duration": {
      const mins = Math.floor(value / 60);
      const secs = Math.round(value % 60);
      return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
    }
    default:
      if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
      if (value >= 1000) return `${(value / 1000).toFixed(1)}k`;
      return value.toLocaleString("cs-CZ");
  }
}

export function MetricCard({ label, value, previousValue, format = "number", icon, subtitle }: MetricCardProps) {
  const change = previousValue && previousValue > 0
    ? ((value - previousValue) / previousValue) * 100
    : null;

  return (
    <Card>
      <CardContent className="px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="min-w-0">
            <p className="text-[11px] font-medium text-muted-foreground leading-none">{label}</p>
            <div className="flex items-baseline gap-2 mt-1">
              <span className="text-xl font-bold leading-none">{formatValue(value, format)}</span>
              {subtitle && <span className="text-[10px] text-muted-foreground">{subtitle}</span>}
              {change !== null && (
                <span className={cn(
                  "text-[11px] font-semibold inline-flex items-center gap-0.5",
                  change > 0 ? "text-green-500" : change < 0 ? "text-red-400" : "text-muted-foreground"
                )}>
                  {change > 0 ? "↑" : change < 0 ? "↓" : ""}
                  {change > 0 ? "+" : ""}{change.toFixed(0)}%
                </span>
              )}
            </div>
          </div>
          {icon && (
            <div className="h-7 w-7 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0">
              {icon}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
